import { action, computed, makeObservable } from 'mobx';

import { getLocalStorageKey, LOCAL_STORAGE_KEYS } from 'config/localStorage';
import { TableViewMode } from 'entities/Table';
import { TableColumnEntity } from 'entities/TableColumn';
import { ToggleStateStore } from 'stores/common/ToggleStateStore';
import { LSConnector, createLSString } from 'stores/common/connectors';

import { ITableFactoryStore } from '../../TableFactoryStore/interface';

import { ITableColumnModel } from './interface';

type TableColumnModelParams = {
  factoryStore: ITableFactoryStore;
  entity: TableColumnEntity;
};

class TableColumnModel implements ITableColumnModel {
  constructor({ entity, factoryStore }: TableColumnModelParams) {
    this._entity = entity;
    this._factoryStore = factoryStore;

    this._visibilityState = new ToggleStateStore({
      defaultValue: entity.visible,
    });

    /**
     * Храним состояние для каждой колонки отдельно в LocalStorage
     * Каждая колонка определяется по двум уникальным идентификаторам - id таблицы и id самой колонки
     * */
    if (factoryStore.callParams?.view === TableViewMode.flat) {
      this._lsStorage = createLSString<boolean>({
        key: `${getLocalStorageKey(LOCAL_STORAGE_KEYS.FLAT_TABLE_COLUMNS)}-${
          this._factoryStore.callParams?.form_id
        }-${this._entity.id}`,
        defaultValue: entity.visible,
        watchValue: () => this._visibilityState.opened,
        onChange: (newValue) => {
          if (newValue !== this._visibilityState.opened) {
            this._visibilityState.toggle();
          }
        },
      });
    }

    makeObservable<TableColumnModel>(this, {
      data: computed,
      visible: computed,

      open: action.bound,
      toggle: action.bound,
      setOpenedState: action.bound,
    });
  }

  private readonly _visibilityState: ToggleStateStore;

  private readonly _factoryStore: ITableFactoryStore;

  private readonly _entity: TableColumnEntity;

  /** Храним для каждой колонки отдельное состояние в LocalStorage */
  private readonly _lsStorage: LSConnector<boolean> | null;

  get visible() {
    return (
      this._factoryStore.tableParams.showNotVisibleByDefaultColumns ||
      this._visibilityState.opened
    );
  }

  get entity(): TableColumnEntity {
    return this._entity;
  }

  get data() {
    return {
      ...this._entity,
      visible: this._visibilityState.opened,
    };
  }

  clone(): TableColumnModel {
    const clone = new TableColumnModel({
      entity: this._entity,
      factoryStore: this._factoryStore,
    });

    if (clone.visible !== this.visible) {
      clone._visibilityState.toggle();
    }

    return clone;
  }

  open(): void {
    this._visibilityState.open();
  }

  toggle(): void {
    this._visibilityState.toggle();
  }

  setOpenedState(newValue: boolean): void {
    if (newValue !== this._visibilityState.opened) {
      this._visibilityState.toggle();
    }
  }

  destroy() {
    this._visibilityState.destroy();
    this._lsStorage?.destroy();
  }
}

export default TableColumnModel;
